import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CoreBack from "../../../../app/core/components/CoreBack/CoreBack";
import CoreButton from "../../../../app/core/components/CoreButton/CoreButton";
import CoreIAicon from "../../../../app/core/components/CoreIAicon/CoreIAicon";
import CoreModal from "../../../../app/core/components/CoreModal/CoreModal";
import CoreSpinner from "../../../../app/core/components/CoreSpinner/CoreSpinner";
import CoreStatus from "../../../../app/core/components/CoreStatus/CoreStatus";
import CoreTableDocCategory from "../../../../app/core/components/CoreTablePractice/CoreTableDocCategory";
import DocComposition from "../../../../app/core/components/DocComposition/DocComposition";
import {
  useDeletePracticeMutation,
  useDoAddNotesPracticeMutation,
  useDoRefreshDocsMutation,
  useGetDocumentsQuery,
  useGetPracticeByIdQuery,
  useServiceDocCompositionMutation,
  useSetStatusMutation,
} from "../../../api/admin/gestisciPraticheApi";
import { useGetUserByIdQuery } from "../../../api/admin/gestisciUtentiApi";
import {
  useCreatePracticeTaskMutation,
  useDeletePracticeTaskMutation,
  useGetPracticeTasksQuery,
  useUpdatePracticeTaskMutation,
} from "../../../api/admin/practiceTaskApi";
import {
  doReportPdfPractice,
  doReportSinglePractice,
  doReportZipPractice,
} from "../GestisciPraticheSlice";
import PracticeTaskManager from "../PracticeTaskManager/PracticeTaskManager";
import FormPratica from "./FormPratica";
import SinglePraticaButtons from "./SinglePraticaButtons";
import StatusModal from "./StatusModal";
import CoreTextArea from "../../../../app/core/components/CoreTextArea/CoreTextArea";

const SinglePraticaPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { Id } = useParams();
  const location = useLocation();
  const appConfig = useSelector((state) => state.app.appConfig);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalDocComposition, setIsModalDocComposition] = useState(false);
  const [singleDoc, setSingleDoc] = useState();
  const [isLoadingTaskOperations, setIsLoadingTaskOperations] = useState(false);
  const [addInfo, setAddInfo] = useState("");
  const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false);

  const [practiceTasksParams, setPracticeTasksParams] = useState({
    practiceId: Id, // Ora practiceId è un parametro separato
    startRow: 0,
    endRow: 10,
    filter: {}, // Filter ora è vuoto di default
    sort: [
      {
        colId: "name",
        sortDirection: "asc",
        sortIndex: 0,
      },
    ],
  });

  const {
    data: practiceTasksData,
    isLoading: isLoadingPracticeTasks,
    refetch: refetchPracticeTasks,
  } = useGetPracticeTasksQuery(practiceTasksParams, {
    skip: !Id || !appConfig?.modules?.practice?.subtask,
  });
  const { data, isLoading, refetch } = useGetPracticeByIdQuery(Id);

  const {
    data: datas,
    isLoading: Loading,
    refetch: refetchDocuments,
  } = useGetDocumentsQuery(Id);

  const { data: userData } = useGetUserByIdQuery(location.state, {
    skip: !location.state,
  });

  // Mutations per le operazioni CRUD
  const [createPracticeTask] = useCreatePracticeTaskMutation();
  const [updatePracticeTask] = useUpdatePracticeTaskMutation();
  const [deletePracticeTask] = useDeletePracticeTaskMutation();
  const [deletePractice] = useDeletePracticeMutation();
  const [setStatus] = useSetStatusMutation();
  const [refreshDocs] = useDoRefreshDocsMutation();
  const [docComposition] = useServiceDocCompositionMutation();
  const [addNotesPractice] = useDoAddNotesPracticeMutation();

  const practiceTasks = practiceTasksData?.items || [];
  const totalTasks = practiceTasksData?.count || 0;
  const aiPrompt = data?.Data?.aiPromptId;

  useEffect(() => {
    if (Id) {
      setPracticeTasksParams((prev) => ({
        ...prev,
        practiceId: Id,
      }));
    }
  }, [Id]);

  useEffect(() => {
    if (data && data.Data && data.Data && data.Data.operatorNotes) {
      setAddInfo(data.Data.operatorNotes);
    }
  }, [data, data?.Data?.operatorNotes]);

  const handleLoadTasks = (params) => {
    setPracticeTasksParams((prev) => ({
      ...prev,
      startRow: params.startRow,
      endRow: params.endRow,
      sort: params.sort,
    }));
  };

  // Funzione per gestire creazione nuova practice task
  const handleCreatePracticeTask = async (taskData) => {
    setIsLoadingTaskOperations(true);
    try {
      await createPracticeTask({
        practiceId: Id,
        ...taskData,
      }).unwrap();

      toast.success(t("TaskCreated"));
      refetchPracticeTasks();
    } catch (error) {
      console.error("Errore creazione practice task:", error);
      toast.error(t("TaskNotCreated"));
    } finally {
      setIsLoadingTaskOperations(false);
    }
  };

  // Funzione per gestire aggiornamento practice task
  const handleUpdatePracticeTask = async (taskId, updatedTaskData) => {
    setIsLoadingTaskOperations(true);
    try {
      await updatePracticeTask({
        id: taskId,
        ...updatedTaskData,
      }).unwrap();

      toast.success(t("TaskUpdated"));
      refetchPracticeTasks();
    } catch (error) {
      console.error("Errore aggiornamento practice task:", error);
      toast.error(t("TaskNotUpdated"));
    } finally {
      setIsLoadingTaskOperations(false);
    }
  };

  // Funzione per gestire eliminazione practice task
  const handleDeletePracticeTask = async (taskId) => {
    setIsLoadingTaskOperations(true);
    try {
      await deletePracticeTask(taskId).unwrap();

      toast.success(t("TaskDeleted"));
      refetchPracticeTasks();
    } catch (error) {
      console.error("Errore eliminazione practice task:", error);
      toast.error(t("TaskNotDeleted"));
    } finally {
      setIsLoadingTaskOperations(false);
    }
  };

  // Funzione per gestire cambio status practice task
  const handleTogglePracticeTaskStatus = async (taskId, newStatus) => {
    setIsLoadingTaskOperations(true);
    try {
      // CORREZIONE: Cerca in practiceTasksData?.items invece di practiceTasksData?.Data
      const currentTask = practiceTasksData?.items?.find(
        (task) => task.id === taskId
      );

      if (!currentTask) {
        console.error("Task non trovato con ID:", taskId);
        throw new Error("Task non trovato");
      }

      await updatePracticeTask({
        id: taskId,
        ...currentTask,
        status: newStatus,
      }).unwrap();

      toast.success(t("TaskStatusUpdated"));
      refetchPracticeTasks();
    } catch (error) {
      console.error("Errore cambio status practice task:", error);
      toast.error(t("TaskStatusNotUpdated"));
    } finally {
      setIsLoadingTaskOperations(false);
    }
  };

  const openDeleteDialog = () => {
    setIsDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleDelete = () => {
    deletePractice(data.Data.id)
      .then(() => {
        toast.success(t("PracticeDeleted"));
        closeDeleteDialog();
        navigate(-1);
      })
      .catch((error) => {
        toast.error(t("PracticeNotDeleted"));
      });
  };

  const handleLensClick = (categoryId) => {
    navigate(
      `/auth/admin/${t(
        "singlePracticeRoute"
      )}/${Id}/documenti?categoria=${categoryId}&userStudioId=${
        userData.Data.studioId
      }`
    );
  };

  const handleDocClick = () => {
    navigate(
      `/auth/admin/${t("singlePracticeRoute")}/${Id}/${t(
        "documents"
      )}?userStudioId=${userData.Data.studioId}`
    );
  };

  const handleUploadDocClick = () => {
    const userObject = {
      name: userData?.Data.name,
      surname: userData?.Data.surname,
      id: userData?.Data.id,
    };

    navigate(`/auth/admin/${t("uploadDocuments")}`, {
      state: userObject,
    });
  };

  const handleOpenTask = () => {
    setIsTaskDetailOpen(true);
  };

  const apiCallForExcelDownload = () => {
    const practiceId = data.Data.id;
    toast.info(t("HandleDownload"));
    dispatch(doReportSinglePractice(practiceId));
  };

  const apiCallForZipDownload = () => {
    const practiceId = data.Data.id;
    toast.info(t("FileViaMail"));
    dispatch(doReportZipPractice(practiceId));
  };

  const apiCallForPdfDownload = () => {
    const practiceId = data.Data.id;
    toast.info(t("FileViaMail"));
    dispatch(doReportPdfPractice(practiceId));
  };

  const handleRefreshDocs = () => {
    const idPratica = data.Data.id;
    refreshDocs(idPratica).then((res) => {
      if (res?.data) toast.info("Assegnazione in corso");
      if (res?.error) toast.error("Errore assegnazione");
    });
    refetchDocuments();
  };

  const handleDocComposition = async () => {
    const response = await toast.promise(docComposition(data.Data.id), {
      pending: t("DocumentComposition") + "...",
      success: {
        render: ({ closeToast }) => (
          <div className="flex justify-between">
            <small>{t("GeneratedIADocument")}</small>
            {response?.data?.Data && (
              <button
                onClick={() => {
                  closeToast();
                  setIsModalDocComposition(true);
                }}
                style={{
                  background: "#3a7a3c",
                  color: "#fff",
                  border: "none",
                  borderRadius: "3px",
                  cursor: "pointer",
                }}
              >
                {t("Visualize")}
              </button>
            )}
          </div>
        ),
      },
      error: t("ReqErr"),
    });
    if (response?.data?.Result === 1) {
      setSingleDoc(response?.data?.Data);
    }
    if (response?.data?.Data === null) {
      toast.error(t("ReqErr"));
    }
    return response;
  };

  const handleAddInfoPractice = () => {
    const id = toast.loading(t("Saving..."), {
      type: "info",
    });

    addNotesPractice({ notes: addInfo, practiceId: Id })
      .unwrap()
      .then((res) => {
        if (res.Result !== 1) {
          toast.update(id, {
            render: res.Exception || t("AddInfoNotCompleted"),
            type: "error",
            autoClose: 1500,
            isLoading: false,
          });
          return;
        }
        toast.update(id, {
          render: t("AddInfoCompleted"),
          type: "success",
          autoClose: 1500,
          isLoading: false,
        });
      })
      .catch(() => {
        toast.update(id, {
          render: t("AddInfoNotCompleted"),
          type: "error",
          autoClose: 1500,
          isLoading: false,
        });
      });
  };

  if (isLoading || Loading) {
    return <CoreSpinner />;
  }

  return (
    <div className="flex flex-col h-full w-full">
      <div className="flex justify-center items-center w-full ">
        <CoreBack />
        <div className="w-11/12 flex items-center justify-between">
          <h2 className="fw-bold">
            {data?.Data?.name} {aiPrompt && <CoreIAicon />}
          </h2>
          <h5 className="flex items-center uppercase space-x-2">
            {t("PracticeStatus")}:{" "}
            <span className="ml-2 space-x-4">
              <CoreStatus status={data?.Data?.status} testo />
            </span>
            <IconButton onClick={() => openModal(Id)}>
              <EditRoundedIcon />
            </IconButton>
          </h5>
        </div>
      </div>
      <SinglePraticaButtons
        appConfig={appConfig}
        handleDocClick={handleDocClick}
        handleUploadClick={handleUploadDocClick}
        apiCallDownload={[
          apiCallForExcelDownload,
          apiCallForZipDownload,
          apiCallForPdfDownload,
        ]}
        openDeleteDialog={openDeleteDialog}
        onRefreshDocs={handleRefreshDocs}
        onRefetch={refetch && refetchDocuments}
        onDocComposition={handleDocComposition}
        onOpenTask={handleOpenTask}
        aiPrompt={aiPrompt}
        reservation={data?.Data?.reservationId}
        status={data?.Data?.status}
      />
      <div className="flex justify-between w-full space-x-4 mt-4">
        <div className="w-4/12">
          <FormPratica practice={data?.Data} userData={userData?.Data} />
        </div>
        <div className="flex w-8/12 space-x-8">
          <CoreTableDocCategory
            getDocList={datas?.Data}
            onNavigate={handleLensClick}
            status={data?.Data?.status}
          />
          <div className="w-1/3 flex flex-col space-y-4">
            <CoreTextArea
              rows={5}
              label={t("AddInfo")}
              name="addInfo"
              value={addInfo}
              onChange={(e) => setAddInfo(e.target.value)}
            />

            <div className="flex justify-around ">
              <CoreButton
                testo={t("Reset")}
                onClick={() => setAddInfo("")}
                className={"btn-round-outline "}
                variant="contained"
              />
              <CoreButton
                testo={t("Invia")}
                onClick={handleAddInfoPractice}
                className={"btn-round bg-green-500 disabled:bg-green-100"}
                variant="contained"
              />
            </div>
          </div>
        </div>
      </div>

      <Dialog open={isDeleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>{t("PracticeDeleting")}</DialogTitle>
        <DialogContent>{t("PracticeDeletingConfirm")}</DialogContent>
        <div className="flex justify-center space-x-4 mb-4">
          <CoreButton
            className={"btn-round bg-red-700 btn-round border-red-700"}
            variant="contained"
            testo={t("Yes")}
            onClick={handleDelete}
          />
          <CoreButton
            className={"btn-round bg-gray-600 btn-round border-gray-600"}
            variant="contained"
            testo={t("No")}
            onClick={closeDeleteDialog}
          />
        </div>
      </Dialog>

      <CoreModal
        component={
          <StatusModal
            setStatus={setStatus}
            closeModal={closeModal}
            praticaId={Id}
          />
        }
        state={isModalOpen}
        onClose={closeModal}
        width={500}
        height={400}
      />
      <CoreModal
        width={"100%"}
        height={"100%"}
        state={isModalDocComposition}
        onClose={() => setIsModalDocComposition(false)}
        component={<DocComposition document={singleDoc} />}
      />

      {/* ===== SEZIONE PRACTICE TASK MANAGER ===== */}
      {appConfig?.modules?.practice?.subtask && (
        <div className="p-4">
          <PracticeTaskManager
            setIsTaskDetailOpen={setIsTaskDetailOpen}
            isTaskDetailOpen={isTaskDetailOpen}
            tasks={practiceTasks}
            totalTasks={totalTasks}
            onAddTask={handleCreatePracticeTask}
            onUpdateTask={handleUpdatePracticeTask}
            onDeleteTask={handleDeletePracticeTask}
            onToggleStatus={handleTogglePracticeTaskStatus}
            onLoadTasks={handleLoadTasks}
            isLoading={isLoadingTaskOperations || isLoadingPracticeTasks}
            readOnly={false}
            pageSize={10}
          />
        </div>
      )}
      {/* ===== FINE SEZIONE PRACTICE TASK MANAGER ===== */}
    </div>
  );
};

export default SinglePraticaPage;
